import { useCustom, useCustomMutation, useNavigation } from "@refinedev/core";
import { Show } from "@refinedev/antd";
import { useParams } from "react-router-dom";
import {
  Button,
  Card,
  Descriptions,
  Image,
  Input,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { useState } from "react";
import { apiUrl } from "@/consts";
import { resolveQueueImageSrc } from "@/util/resolveQueueImageSrc";
import { ImageUpload } from "@/components/ImageUpload";
import type { SurveySubmissionRow, SurveySubmissionStatus } from "./types";

const { Text } = Typography;

const STATUS_COLOR: Record<SurveySubmissionStatus, string> = {
  NOT_SUBMITTED: "default",
  PENDING_REVIEW: "gold",
  ACCEPTED: "success",
  REJECTED: "red",
};

function getScoreColor(score: number): string {
  if (score >= 80) return "#52c41a";
  if (score >= 60) return "#faad14";
  return "#f5222d";
}

function apiError(err: unknown): string {
  const e = err as { message?: string; response?: { data?: { error?: string } } };
  return e?.response?.data?.error || e?.message || "Request failed";
}

type PlayerFormPortalLinkResponse = {
  portalUrl: string;
  expiresIn: number;
};

export function SurveySubmissionShow() {
  const { id } = useParams<{ id: string }>();
  const { show: navShow } = useNavigation();
  const { mutate, isLoading: mutating } = useCustomMutation();
  const { mutate: mutatePortalLink, isLoading: portalLinkLoading } = useCustomMutation();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [imageOpen, setImageOpen] = useState(false);
  const [uploadedImageId, setUploadedImageId] = useState("");

  const submissionQuery = useCustom<{ submission: SurveySubmissionRow }>({
    url: `${apiUrl}/survey-submissions/${id}`,
    method: "get",
    queryOptions: { enabled: Boolean(id) },
  });

  const s = submissionQuery.data?.data?.submission;
  const loading = submissionQuery.isLoading;
  const refetch = submissionQuery.refetch;

  const canReview = s?.status === "PENDING_REVIEW";
  const submittedImageSrc = resolveQueueImageSrc(s?.player?.originalPlayerPhoto);
  const adminImageSrc = resolveQueueImageSrc(s?.playerImageUrl);

  function handleAccept() {
    if (!id) return;
    mutate(
      { url: `${apiUrl}/survey-submissions/${id}/accept`, method: "patch", values: {} },
      {
        onSuccess: (res) => {
          message.success(
            (res as { data?: { message?: string } })?.data?.message ||
            "Submission accepted"
          );
          refetch?.();
        },
        onError: (err) => message.error(apiError(err)),
      }
    );
  }

  function handleReject() {
    if (!id) return;
    const reason = rejectReason.trim();
    if (reason.length < 1) {
      message.error("Rejection reason is required");
      return;
    }
    mutate(
      {
        url: `${apiUrl}/survey-submissions/${id}/reject`,
        method: "patch",
        values: { rejectionReason: reason },
      },
      {
        onSuccess: (res) => {
          message.success(
            (res as { data?: { message?: string } })?.data?.message ||
            "Submission rejected"
          );
          setRejectOpen(false);
          setRejectReason("");
          refetch?.();
        },
        onError: (err) => message.error(apiError(err)),
      }
    );
  }

  function handlePlayerFormLink() {
    if (!id) return;
    mutatePortalLink(
      {
        url: `${apiUrl}/survey-submissions/${id}/player-form-portal-link`,
        method: "post",
        values: {},
      },
      {
        onSuccess: (res) => {
          const data = (res as { data?: PlayerFormPortalLinkResponse })?.data;
          if (!data?.portalUrl) {
            message.error("No portal URL returned");
            return;
          }
          const mins = Math.max(1, Math.round(data.expiresIn / 60));
          window.open(data.portalUrl, "_blank", "noopener,noreferrer");
          message.success(`Portal opened in a new tab. Link expires in about ${mins} minutes.`);
        },
        onError: (err) => message.error(apiError(err)),
      },
    );
  }

  function handleEditImage() {
    if (!id) return;
    if (!uploadedImageId) {
      message.error("Upload an image first");
      return;
    }
    mutate(
      {
        url: `${apiUrl}/survey-submissions/${id}/edit-image`,
        method: "patch",
        values: { playerImageUrl: uploadedImageId },
      },
      {
        onSuccess: () => {
          message.success("Image updated");
          setImageOpen(false);
          setUploadedImageId("");
          refetch?.();
        },
        onError: (err) => message.error(apiError(err)),
      }
    );
  }

  return (
    <Show
      isLoading={loading}
      title="Survey submission"
      resource="survey-submissions"
    >
      {s ? (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Space wrap size="small">
            {canReview && (
              <Popconfirm
                title="Accept this submission?"
                onConfirm={handleAccept}
                okButtonProps={{ loading: mutating }}
              >
                <Button type="primary" loading={mutating}>
                  Accept
                </Button>
              </Popconfirm>
            )}
            {canReview && (
              <Button
                danger
                onClick={() => {
                  setRejectReason("");
                  setRejectOpen(true);
                }}
              >
                Reject
              </Button>
            )}
            <Button
              onClick={() => {
                setUploadedImageId("");
                setImageOpen(true);
              }}
            >
              Upload player image
            </Button>
            <Button loading={portalLinkLoading} onClick={handlePlayerFormLink}>
              Open portal form (as player)
            </Button>
          </Space>

          <Card size="small">
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLOR[s.status]}>{s.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Submitted">
                {s.submittedAt ? new Date(s.submittedAt).toLocaleString() : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Reviewed">
                {s.reviewedAt ? new Date(s.reviewedAt).toLocaleString() : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Rejection reason">
                {s.rejectionReason ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Name">
                {s.player?.fullName || s.player?.username || s.userId}
              </Descriptions.Item>
              <Descriptions.Item label="Email">{s.player?.email ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="Event">
                {s.event?.name ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Date">
                {s.event?.date ? new Date(s.event.date).toLocaleString() : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Location">{s.event?.location ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="Event action">
                {s.eventId ? (
                  <Button size="small" onClick={() => navShow("scouting-event", s.eventId)}>
                    Open scouting event
                  </Button>
                ) : "—"}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {(submittedImageSrc || adminImageSrc) && (
            <Card size="small" title="Player images">
              <Space wrap size="middle">
                {submittedImageSrc && (
                  <div>
                    <Text strong>Submitted survey image</Text>
                    <div style={{ marginTop: 8 }}>
                      <Image
                        width={200}
                        src={submittedImageSrc}
                        alt="Submitted player"
                        style={{ maxHeight: 240, objectFit: "contain" }}
                      />
                    </div>
                  </div>
                )}
                {adminImageSrc && (
                  <div>
                    <Text strong>Admin uploaded image</Text>
                    <div style={{ marginTop: 8 }}>
                      <Image
                        width={200}
                        src={adminImageSrc}
                        alt="Admin uploaded player"
                        style={{ maxHeight: 240, objectFit: "contain" }}
                      />
                    </div>
                  </div>
                )}
              </Space>
            </Card>
          )}

          {s.scoutingScore && (
            <Card size="small" title="Scouting score">
              <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
                <Descriptions.Item label="Age group">{s.scoutingScore.ageGroup ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Overall rating">
                  <Text strong style={{ color: getScoreColor(s.scoutingScore.overallRating ?? 0) }}>
                    {s.scoutingScore.overallRating ?? 0}%
                  </Text>
                </Descriptions.Item>
              </Descriptions>
              <Table
                size="small"
                pagination={false}
                dataSource={[
                  { metric: "Power", score: s.scoutingScore.power },
                  { metric: "Acceleration", score: s.scoutingScore.acceleration },
                  { metric: "Speed", score: s.scoutingScore.speed },
                  { metric: "Accuracy", score: s.scoutingScore.accuracy },
                  { metric: "Agility", score: s.scoutingScore.agility },
                  { metric: "Dribble", score: s.scoutingScore.dribble },
                ]}
                rowKey="metric"
                columns={[
                  { title: "Metric", dataIndex: "metric" },
                  {
                    title: "Score",
                    dataIndex: "score",
                    width: 120,
                    render: (score: number) => (
                      <Text strong style={{ color: getScoreColor(score) }}>
                        {score}%
                      </Text>
                    ),
                  },
                ]}
              />
            </Card>
          )}
        </Space>
      ) : null}

      <Modal
        title="Reject submission"
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        onOk={handleReject}
        confirmLoading={mutating}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          Rejection reason
        </Text>
        <Input.TextArea
          rows={4}
          placeholder="Explain what needs improvement"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          maxLength={500}
          showCount
          status={rejectReason.trim().length === 0 ? "error" : ""}
        />
      </Modal>

      <Modal
        title="Upload player image"
        open={imageOpen}
        onCancel={() => setImageOpen(false)}
        onOk={handleEditImage}
        confirmLoading={mutating}
      >
        <ImageUpload
          uploadType="PROFILE"
          accept="image/*"
          onChange={(fileId) => setUploadedImageId(fileId)}
        />
      </Modal>
    </Show>
  );
}
